import React, { useState, useEffect } from 'react';
import './History.css';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

function History() {
  const [detections, setDetections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDetection, setSelectedDetection] = useState(null);
  
  useEffect(() => {
    fetchHistory();
  }, []);
  
  async function fetchHistory() {
    setLoading(true);
    setError(null);
    
    try {
      const res = await fetch(`${API_URL}/api/detections?limit=100`);
      if (!res.ok) throw new Error('Failed to fetch history');
      
      const data = await res.json();
      setDetections(data.detections || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }
  
  async function handleDelete(detectionId) {
    if (!confirm('Delete this detection?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/detections/${detectionId}`, {
        method: 'DELETE',
      });
      
      if (res.ok) {
        setDetections(detections.filter(d => d.id !== detectionId));
      } else {
        alert('Failed to delete');
      }
    } catch (err) {
      alert('Error: ' + err.message);
    }
  }
  
  function handleView(detection) {
    setSelectedDetection(detection);
  }
  
  function formatDate(timestamp) {
    return new Date(timestamp).toLocaleString();
  }
  
  if (loading) {
    return <div className="loading">Loading history...</div>;
  }
  
  if (error) {
    return <div className="error">Error: {error}</div>;
  }
  
  return (
    <div className="history-page">
      <h1>📜 Detection History</h1>
      
      {detections.length === 0 ? (
        <div className="empty-state">
          <p>No detection history yet.</p>
          <p>Test a model and save results to see them here.</p>
        </div>
      ) : (
        <div className="history-table-container">
          <table className="history-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Model</th>
                <th>Detections</th>
                <th>Threshold</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {detections.map((det) => (
                <tr key={det.id}>
                  <td>{formatDate(det.created_at)}</td>
                  <td>
                    {det.model_name} v{det.model_version}
                  </td>
                  <td>{det.detections_count}</td>
                  <td>{(det.confidence_threshold * 100).toFixed(0)}%</td>
                  <td className="actions">
                    <button onClick={() => handleView(det)}>👁️ View</button>
                    <button onClick={() => handleDelete(det.id)}>🗑️</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {selectedDetection && (
        <DetectionModal
          detection={selectedDetection}
          onClose={() => setSelectedDetection(null)}
        />
      )}
    </div>
  );
}

function DetectionModal({ detection, onClose }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Detection Details</h2>
          <button onClick={onClose}>✕</button>
        </div>
        
        <div className="modal-body">
          <div className="detection-info">
            <p><strong>Model:</strong> {detection.model_name} v{detection.model_version}</p>
            <p><strong>Date:</strong> {new Date(detection.created_at).toLocaleString()}</p>
            <p><strong>Detections:</strong> {detection.detections_count}</p>
            <p><strong>Threshold:</strong> {(detection.confidence_threshold * 100).toFixed(0)}%</p>
          </div>
          
          <h3>Detections ({detection.detections_json.length})</h3>
          <div className="detections-list">
            {detection.detections_json.map((det, idx) => (
              <div key={idx} className="detection-item">
                <span className="detection-index">#{idx + 1}</span>
                <span className="detection-class">Class {det.class}</span>
                <span className="detection-confidence">
                  {(det.confidence * 100).toFixed(1)}%
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default History;

